
import React, { useMemo } from 'react';
import { ExternalLink, ArrowRightLeft, Workflow, Settings, Variable, Zap } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData, CallableProcess, CallableStartEvent } from '../../../types';
import { useEditorConfig } from '../../../contexts/EditorContext';

interface CallActivityTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

const StartEventSection: React.FC<{ startEvents: CallableStartEvent[] }> = ({ startEvents }) => {
    if (startEvents.length === 0) return null;

    return (
        <div className="mt-3 space-y-2">
            {startEvents.map((se) => (
                <div key={se.id} className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                            {se.type}
                        </span>
                        <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{se.name}</span>
                    </div>

                    {se.type === 'form' && se.fields && se.fields.length > 0 && (
                        <div className="mt-2">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Required Fields</p>
                            <div className="space-y-1">
                                {se.fields.map((f) => (
                                    <div key={f.name} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                        <span className="font-mono text-[11px]">{f.name}</span>
                                        <span className="text-slate-400">:</span>
                                        <span className="text-slate-500">{f.type}</span>
                                        {f.required && <span className="text-red-400 text-[10px]">*</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {se.type === 'message' && (
                        <div className="mt-2 space-y-1">
                            {se.messageName && (
                                <p className="text-xs text-slate-600 dark:text-slate-400">
                                    <span className="text-slate-500">Message:</span> <span className="font-mono">{se.messageName}</span>
                                </p>
                            )}
                            {se.messagePayloadSchema && se.messagePayloadSchema.length > 0 && (
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Payload Schema</p>
                                    <div className="space-y-1">
                                        {se.messagePayloadSchema.map((f) => (
                                            <div key={f.name} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                                <span className="font-mono text-[11px]">{f.name}</span>
                                                <span className="text-slate-400">:</span>
                                                <span className="text-slate-500">{f.type}</span>
                                                {f.required && <span className="text-red-400 text-[10px]">*</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {se.type === 'signal' && se.signalName && (
                        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                            <span className="text-slate-500">Signal:</span> <span className="font-mono">{se.signalName}</span>
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

export const CallActivityTab: React.FC<CallActivityTabProps> = ({ data, onChange }) => {
    const { services } = useEditorConfig();
    const callableProcesses = services.callableProcesses;

    const collectionOptions = useMemo(() => {
        if (!callableProcesses) return undefined;
        return [...new Set(callableProcesses.map(p => p.collection))].filter(Boolean)
            .map(c => ({ value: c, label: c }));
    }, [callableProcesses]);

    const processOptions = useMemo(() => {
        if (!callableProcesses) return undefined;
        const filtered = data.processCollection
            ? callableProcesses.filter(p => p.collection === data.processCollection)
            : callableProcesses;
        return [...new Set(filtered.map(p => p.processName))].filter(Boolean)
            .map(n => ({ value: n, label: n }));
    }, [callableProcesses, data.processCollection]);

    const matchedProcess = useMemo<CallableProcess | undefined>(() => {
        if (!callableProcesses || !data.processName) return undefined;
        return callableProcesses.find(
            p => p.processName === data.processName &&
                (!data.processCollection || p.collection === data.processCollection)
        );
    }, [callableProcesses, data.processName, data.processCollection]);

    const collectionFieldType = collectionOptions ? 'combobox' : 'text';
    const processFieldType = processOptions ? 'combobox' : 'text';

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-100 dark:border-sky-900 flex gap-3">
                <Workflow className="text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-sky-800 dark:text-sky-300">External Process</p>
                    <p className="text-[11px] text-sky-700 dark:text-sky-400 leading-relaxed">
                        Calls another process definition. The parent process waits until the called process completes.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <ExternalLink size={12} /> Target Process
                    </h3>
                    <PropertyField
                        config={{
                            name: 'processCollection',
                            label: 'Collection',
                            type: collectionFieldType,
                            placeholder: 'MyCollection',
                            description: 'The collection namespace of the target process.',
                            ...(collectionOptions ? { options: collectionOptions } : {})
                        }}
                        value={data.processCollection}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{
                            name: 'processName',
                            label: 'Process Name',
                            type: processFieldType,
                            placeholder: 'MyProcess/sub1',
                            validation: { required: true },
                            description: 'The unique name of the target process.',
                            ...(processOptions ? { options: processOptions } : {})
                        }}
                        value={data.processName}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{
                            name: 'processVersion',
                            label: 'Version',
                            type: 'number',
                            placeholder: '0',
                            validation: { min: -1 },
                            description: '0 for current, -1 for latest.'
                        }}
                        value={data.processVersion}
                        onChange={onChange}
                    />
                </div>

                {matchedProcess && matchedProcess.startEvents.length > 0 && (
                    <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-amber-200 dark:border-amber-800 pb-2 mb-2">
                            <Zap size={12} /> Start Events
                        </h3>
                        <p className="text-[11px] text-slate-600 dark:text-slate-400">
                            The target process defines the following start events. Configure input variables in the Data Mapping section below to supply required fields.
                        </p>
                        <StartEventSection startEvents={matchedProcess.startEvents} />
                    </div>
                )}

                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <Settings size={12} /> Execution Settings
                    </h3>
                    <PropertyField
                        config={{
                            name: 'keepBusinessKey',
                            label: 'Keep Business Key',
                            type: 'boolean',
                            description: 'Propagate the parent process business key to the child process.'
                        }}
                        value={data.keepBusinessKey}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{
                            name: 'sequentialLoop',
                            label: 'Sequential Loop',
                            type: 'boolean',
                            description: 'If looping, execute iterations sequentially instead of parallel.'
                        }}
                        value={data.sequentialLoop}
                        onChange={onChange}
                    />
                    {data.sequentialLoop && (
                        <div className="ml-4 space-y-4 pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                            <PropertyField
                                config={{
                                    name: 'collectionName',
                                    label: 'Collection Name',
                                    type: 'text',
                                    placeholder: 'myList',
                                    description: 'Variable containing the collection to iterate over.'
                                }}
                                value={data.collectionName}
                                onChange={onChange}
                            />
                            <PropertyField
                                config={{
                                    name: 'elementName',
                                    label: 'Element Name',
                                    type: 'text',
                                    placeholder: 'item',
                                    description: 'Variable name for the current loop element.'
                                }}
                                value={data.elementName}
                                onChange={onChange}
                            />
                        </div>
                    )}
                    {/* AsAsyncCall is hardcoded to false by the engine — field omitted from UI */}
                </div>

                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <Variable size={12} /> Result
                    </h3>
                    <PropertyField
                        config={{
                            name: 'resultVariable',
                            label: 'Result Variable',
                            type: 'text',
                            placeholder: 'callResult',
                            description: 'Variable to store the returned values from the called process as an object.'
                        }}
                        value={data.resultVariable}
                        onChange={onChange}
                    />
                </div>

                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <ArrowRightLeft size={12} /> Data Mapping
                    </div>

                    <PropertyField
                        config={{
                            name: 'inputVariables',
                            label: 'Input Variables (Map to Child)',
                            type: 'object',
                            rows: 4,
                            placeholder: '{\n  "childVar": "${parentVar}"\n}',
                            description: 'Map parent variables to child process inputs.'
                        }}
                        value={data.inputVariables}
                        onChange={onChange}
                    />

                    <PropertyField
                        config={{
                            name: 'outputVariables',
                            label: 'Output Variables (Map from Child)',
                            type: 'object',
                            rows: 4,
                            placeholder: '{\n  "parentVar": "${childResult}"\n}',
                            description: 'Map child process outputs back to parent variables.'
                        }}
                        value={data.outputVariables}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
};
